<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router/composables';
  import GpthelpWidget from '@/components/gpthelp/GpthelpWidget.vue';
  import { useAssistantShortcut } from '@/composition/assistant/use-assistant-shortcut';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import { useAuthStore } from '@/composition/use-auth';
  import V2AssistantCoachmark from './V2AssistantCoachmark.vue';
  import V2AssistantPanel from './V2AssistantPanel.vue';

  /**
   * Where the GPTHelp chat lives (plan §9.2, §9.14): the «Asistente» panel
   * (with its coachmark and Ctrl/⌘+K) for logged-in users inside the stores
   * the rollout enables; the legacy bubble on every page without the panel
   * (logged-out pages, pages outside a store), before and after GA, on the
   * browsers that can run it. POS workspaces show neither. Only one of them
   * is ever mounted.
   */
  const store = useAssistantStore();
  const authStore = useAuthStore();
  const route = useRoute();
  const router = useRouter();
  const panel = ref<InstanceType<typeof V2AssistantPanel> | null>(null);

  /**
   * App.vue renders before the first navigation resolved, i.e. before the
   * route guard loaded the store: deciding only afterwards keeps stores with
   * the panel from mounting (and loading) the bubble for a moment.
   */
  router.onReady(
    () => store.setRouteReady(),
    () => store.setRouteReady()
  );

  /**
   * A new bubble for every identity (plan §9.11): at logout or a user switch,
   * the previous user's chat must not stay on a shared device. The store's
   * identity watcher (created with the store, before this component's render
   * watcher) clears the `gpthelp-*` keys first, so the new element starts
   * empty.
   */
  const bubbleKey = computed(() => authStore.user?.id ?? 'guest');

  // No panel on POS workspaces and chromeless flows; no bubble on POS
  // workspaces.
  watch(
    () => route.meta,
    (meta) => {
      store.setRouteAllowed(!meta?.pos && !meta?.noMenus);
      store.setPosRoute(!!meta?.pos);
    },
    { immediate: true }
  );

  // The welcome suggestions depend on the page.
  watch(
    () => route.fullPath,
    () => {
      panel.value?.dispatchToWidget('gpthelp:context-changed');
    }
  );

  useAssistantShortcut({
    available: () => store.available,
    isOpen: () => store.shown,
    open: () => store.openPanel('shortcut'),
    close: () => store.closePanel(),
    containsFocus: () => panel.value?.containsFocus() ?? false,
    focusComposer: () => panel.value?.focusComposer(),
  });
</script>

<template>
  <div v-if="store.routeReady && store.enabled" class="v2-assistant-root">
    <V2AssistantPanel ref="panel" />
    <V2AssistantCoachmark />
  </div>
  <!-- store.bubbleShown gates the drawer's room for the bubble too. -->
  <GpthelpWidget v-else-if="store.bubbleShown" :key="bubbleKey" />
</template>
